import { REST_schema } from "../../@types/sharedSchema";

/**
 * A failed sales-recruiting request (`sendSalesRecruitingRequest`), carrying the server's own
 * `status` and `code` — a 409 NOT_ALLOWED ("their link is already with them") is something the
 * page says in words, and a 403 means the page should not be open at all.
 *
 * Mirrors `SalesMapError`: a bare Error with the status interpolated into the message is
 * readable in a console and useless in code.
 */
export class SalesRecruitingError extends Error {
  /** HTTP status. `0` when the request never got a response (offline, DNS, CORS). */
  readonly status: number;
  /** The server's `code` field from `SalesRecruitingErrorResponse`, when the body was JSON. */
  readonly code: REST_schema.SalesRecruitingErrorResponse["code"] | null;

  constructor(
    message: string,
    status: number,
    code: REST_schema.SalesRecruitingErrorResponse["code"] | null | undefined
  ) {
    super(message);
    this.name = "SalesRecruitingError";
    this.status = status;
    this.code = code ?? null;
    // tsconfig targets es5, where subclassing a built-in breaks the prototype chain
    // and `instanceof` silently returns false.
    Object.setPrototypeOf(this, SalesRecruitingError.prototype);
  }
}

export function isSalesRecruitingError(error: unknown): error is SalesRecruitingError {
  if (error instanceof SalesRecruitingError) return true;
  return (error as { name?: string } | null)?.name === "SalesRecruitingError";
}
